/**
 * Pure geometry builder functions for custom mesh creation.
 * Extracted from BabylonScene.js. These functions generate raw vertex data
 * (positions, indices, normals, uvs) without any Babylon.js scene dependency.
 */

/**
 * Build a custom hollow/partial cylinder geometry.
 * Used by both 'cylinder' and 'pipe' mesh types.
 *
 * @param {number} outerR      – Outer radius
 * @param {number} innerR      – Inner radius (0 = solid)
 * @param {number} halfH       – Half-height
 * @param {number} segments    – Number of circumference segments
 * @param {number} arcAngle    – Arc angle in degrees (360 = full)
 * @param {boolean} topCap     – Generate top cap
 * @param {boolean} bottomCap  – Generate bottom cap
 * @returns {{ positions: number[], indices: number[], normals: number[], uvs: number[] }}
 */
export function buildHollowCylinderGeometry(outerR, innerR, halfH, segments, arcAngle, topCap, bottomCap) {
  const positions = [];
  const indices = [];
  const normals = [];
  const uvs = [];
  const arcRad = (arcAngle * Math.PI) / 180;
  const isHollow = innerR > 0;
  const isPartial = arcAngle < 360;

  let vi = 0;
  const addVert = (x, y, z, nx, ny, nz, u, v) => {
    positions.push(x, y, z);
    normals.push(nx, ny, nz);
    uvs.push(u, v);
    return vi++;
  };

  // OUTER SURFACE
  const outerStart = vi;
  for (let i = 0; i <= segments; i++) {
    const angle = (i / segments) * arcRad;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const u = i / segments;
    addVert(outerR * cos, -halfH, outerR * sin, cos, 0, sin, u, 0);
    addVert(outerR * cos, halfH, outerR * sin, cos, 0, sin, u, 1);
  }
  for (let i = 0; i < segments; i++) {
    const b = outerStart + i * 2;
    indices.push(b, b + 1, b + 3);
    indices.push(b, b + 3, b + 2);
  }

  // INNER SURFACE (if hollow)
  if (isHollow) {
    const innerStart = vi;
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * arcRad;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const u = i / segments;
      addVert(innerR * cos, -halfH, innerR * sin, -cos, 0, -sin, u, 0);
      addVert(innerR * cos, halfH, innerR * sin, -cos, 0, -sin, u, 1);
    }
    for (let i = 0; i < segments; i++) {
      const b = innerStart + i * 2;
      indices.push(b, b + 3, b + 1);
      indices.push(b, b + 2, b + 3);
    }
  }

  // TOP CAP
  if (topCap) {
    if (isHollow) {
      const topStart = vi;
      for (let i = 0; i <= segments; i++) {
        const angle = (i / segments) * arcRad;
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        const u = i / segments;
        addVert(outerR * cos, halfH, outerR * sin, 0, 1, 0, u, 1);
        addVert(innerR * cos, halfH, innerR * sin, 0, 1, 0, u, 0);
      }
      for (let i = 0; i < segments; i++) {
        const b = topStart + i * 2;
        indices.push(b, b + 2, b + 3);
        indices.push(b, b + 3, b + 1);
      }
    } else {
      const center = addVert(0, halfH, 0, 0, 1, 0, 0.5, 0.5);
      const topDiscStart = vi;
      for (let i = 0; i <= segments; i++) {
        const angle = (i / segments) * arcRad;
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        addVert(outerR * cos, halfH, outerR * sin, 0, 1, 0, 0.5 + cos * 0.5, 0.5 + sin * 0.5);
      }
      for (let i = 0; i < segments; i++) {
        indices.push(center, topDiscStart + i, topDiscStart + i + 1);
      }
    }
  }

  // BOTTOM CAP
  if (bottomCap) {
    if (isHollow) {
      const botStart = vi;
      for (let i = 0; i <= segments; i++) {
        const angle = (i / segments) * arcRad;
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        const u = i / segments;
        addVert(outerR * cos, -halfH, outerR * sin, 0, -1, 0, u, 1);
        addVert(innerR * cos, -halfH, innerR * sin, 0, -1, 0, u, 0);
      }
      for (let i = 0; i < segments; i++) {
        const b = botStart + i * 2;
        indices.push(b, b + 3, b + 2);
        indices.push(b, b + 1, b + 3);
      }
    } else {
      const center = addVert(0, -halfH, 0, 0, -1, 0, 0.5, 0.5);
      const botDiscStart = vi;
      for (let i = 0; i <= segments; i++) {
        const angle = (i / segments) * arcRad;
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        addVert(outerR * cos, -halfH, outerR * sin, 0, -1, 0, 0.5 + cos * 0.5, 0.5 + sin * 0.5);
      }
      for (let i = 0; i < segments; i++) {
        indices.push(center, botDiscStart + i + 1, botDiscStart + i);
      }
    }
  }

  // ARC END CAPS (flat faces at the cut edges when arc < 360)
  if (isPartial) {
    // Start edge (angle = 0)
    if (isHollow) {
      const s0 = addVert(outerR, -halfH, 0, 0, 0, -1, 0, 0);
      const s1 = addVert(outerR, halfH, 0, 0, 0, -1, 0, 1);
      const s2 = addVert(innerR, halfH, 0, 0, 0, -1, 1, 1);
      const s3 = addVert(innerR, -halfH, 0, 0, 0, -1, 1, 0);
      indices.push(s0, s2, s1);
      indices.push(s0, s3, s2);
    } else {
      const s0 = addVert(0, -halfH, 0, 0, 0, -1, 0, 0);
      const s1 = addVert(outerR, -halfH, 0, 0, 0, -1, 1, 0);
      const s2 = addVert(outerR, halfH, 0, 0, 0, -1, 1, 1);
      const s3 = addVert(0, halfH, 0, 0, 0, -1, 0, 1);
      indices.push(s0, s2, s1);
      indices.push(s0, s3, s2);
    }

    // End edge (angle = arcRad)
    const eCos = Math.cos(arcRad);
    const eSin = Math.sin(arcRad);
    const nx = -eSin;
    const nz = eCos;
    if (isHollow) {
      const e0 = addVert(outerR * eCos, -halfH, outerR * eSin, nx, 0, nz, 0, 0);
      const e1 = addVert(outerR * eCos, halfH, outerR * eSin, nx, 0, nz, 0, 1);
      const e2 = addVert(innerR * eCos, halfH, innerR * eSin, nx, 0, nz, 1, 1);
      const e3 = addVert(innerR * eCos, -halfH, innerR * eSin, nx, 0, nz, 1, 0);
      indices.push(e0, e1, e2);
      indices.push(e0, e2, e3);
    } else {
      const e0 = addVert(0, -halfH, 0, nx, 0, nz, 0, 0);
      const e1 = addVert(outerR * eCos, -halfH, outerR * eSin, nx, 0, nz, 1, 0);
      const e2 = addVert(outerR * eCos, halfH, outerR * eSin, nx, 0, nz, 1, 1);
      const e3 = addVert(0, halfH, 0, nx, 0, nz, 0, 1);
      indices.push(e0, e1, e2);
      indices.push(e0, e2, e3);
    }
  }

  return { positions, indices, normals, uvs };
}
